import { AsyncPipe, DatePipe, JsonPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { doc, Firestore, Timestamp, updateDoc } from '@angular/fire/firestore';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import axios from 'axios';
import dayjs from 'dayjs';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Observable, Subject, take, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group, GroupType } from '../../../../models/group.model';
import { SchoolMemberType } from '../../../../models/member.school.model';
import { NoticeAiData } from '../../../../models/notice-zod.model';
import {
  Notice,
  NoticeAttachment,
  NoticeStatus,
} from '../../../../models/notice.model';
import { UserModel } from '../../../../models/user.model';
import { NoticeService } from '../../../../services/notice.service';
import { UtilsService } from '../../../../services/utils.service';
import * as noticeActions from '../../../../state/actions/notice.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as noticeSelector from '../../../../state/selectors/notice.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnChipComponent } from '../../../components/buttons/btn-chip/btn-chip.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { ImageEditorComponent } from '../../../components/image-editor/image-editor.component';
import {
  EditorScene,
  LayerSummary,
} from '../../../components/image-editor/models/editor-layer.model';
import { CheckboxComponent } from '../../../components/inputs/checkbox/checkbox.component';
import { S3ImageUploadComponent } from '../../../components/uploaders/s3-image-upload/s3-image-upload.component';

@Component({
  selector: 'app-upsert-notice',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    JsonPipe,
    AsyncPipe,
    DatePipe,
    NgClass,
    NgTemplateOutlet,
    BtnBackComponent,
    BtnSubmitComponent,
    BtnFabComponent,
    BtnChipComponent,
    CheckboxComponent,
    S3ImageUploadComponent,
    ImageEditorComponent,
  ],
  templateUrl: './upsert-notice.component.html',
  styleUrl: './upsert-notice.component.css',
})
export class UpsertNoticeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('imageUploader') imageUploader?: S3ImageUploadComponent;
  @ViewChild('imageEditor') imageEditorRef?: ImageEditorComponent;
  @ViewChild('chatContainer') chatContainerRef?: ElementRef<HTMLDivElement>;

  cdr = inject(ChangeDetectorRef);
  private fbAuth = inject(Auth);
  private utilsService = inject(UtilsService);
  private noticeService = inject(NoticeService);

  private dialog: MatDialog = inject(MatDialog);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  store: Store<AppState> = inject(Store<AppState>);
  toastr: ToastrService = inject(ToastrService);
  private logger: NGXLogger = inject(NGXLogger);
  afs: Firestore = inject(Firestore);
  fb = inject(FormBuilder);

  pageForm: FormGroup;

  uuid: string = uuidv4();

  user: UserModel | null = null;

  myRoles: any;

  noticeId: string | null = null;
  noticeData: Notice | null | undefined;

  groupId: string | null = null;
  group: Group | null | undefined = null;
  grades$: Observable<Grade[]> = new Observable<Grade[]>();

  progress: number = 0;
  message: string = '';

  preSignedUrlEndpoint: string = environment.AWS_SCHOOL_NOTICE_IMAGE_SIGNEDURL;
  apiKey: string = environment.AWS_API_KEY;
  metadata: any = {
    name: this.uuid,
    noticeId: this.uuid,
  };
  cloudFrontDomain = environment.AWS_GROUPS_CLOUDFRONT;

  currentNoticeImage: string | null | undefined;
  currentImageScene?: EditorScene;

  working: boolean = false;

  exit$ = new Subject<void>();

  // AI chat drawer state — the panel lives in a fixed window that slides in
  // from the right edge of the page (see .chat-drawer / .ai-fab in the
  // stylesheet), toggled via the floating action button. It stays mounted
  // and available regardless of which tab below is active.
  chatOpen: boolean = false;

  // Notice Details / Notice Image tabs — the image editor used to be a
  // modal overlay on top of this same component instance; it's now an
  // inline tab instead (still the SAME component instance, so form/editor
  // state is never lost switching back and forth). imageEditorMounted stays
  // true forever once the editor is first needed (tab click, or an AI
  // background auto-generation) — it's never torn down again, so in-progress
  // unsaved layer edits survive switching back to Notice Details and
  // returning.
  activeTab: 'form' | 'image' = 'form';
  imageEditorMounted: boolean = false;
  get showImageEditor(): boolean {
    return this.activeTab === 'image';
  }
  showPreviewModal: boolean = false;

  aiPromptInput: string = '';
  aiGenerating: boolean = false;
  aiTextCallCount: number = 0;
  showSaveStyleDialog: boolean = false;
  latestGeneratedStylePrompt?: string;
  styleNameInput: string = '';
  isSavingStyle: boolean = false;
  styleSavedSuccessfully: boolean = false;

  chatHistory: {
    role: 'user' | 'assistant';
    text: string;
    noticeData?: NoticeAiData;
    imageUrl?: string;
    chatActions?: { label: string; icon: string; type: 'edit_image' | 'save' }[];
    noticePreview?: { title: string; description: string };
  }[] = [];
  isRecordingVoice: boolean = false;
  recognition: any = null;

  // AI state
  hasDraft: boolean = false;
  aiImageGenerating: boolean = false;
  aiImageTheme: string = '';

  quickPrompts: string[] = [
    'Sunday School Picnic on Saturday at 10 AM',
    'Youth Fellowship Prayer Meeting',
    'Group Budget & Quarterly Finance Update',
    'Urgent Announcement for Group Members',
  ];

  get memberTypesList(): string[] {
    if (this.group?.groupType === 'church') {
      return ['admin', 'member', 'treasurer'];
    }
    return [
      SchoolMemberType.parent,
      SchoolMemberType.student,
      SchoolMemberType.teacher,
    ];
  }
  noticeStatuses = Object.values(NoticeStatus);

  existingAttachments: NoticeAttachment[] = [];
  newAttachments: {
    file: File;
    name: string;
    progress: number;
    uploading: boolean;
    error?: string;
  }[] = [];

  constructor() {
    this.pageForm = this.fb.group({
      title: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
      ]),
      description: new FormControl('', [
        Validators.required,
        Validators.maxLength(5000),
      ]),
      publishOn: new FormControl(dayjs().format('YYYY-MM-DDTHH:mm'), [
        Validators.required,
      ]),
      removeAfter: new FormControl(
        dayjs().add(30, 'days').format('YYYY-MM-DDTHH:mm'),
        [Validators.required],
      ),
      noticeStatus: new FormControl(NoticeStatus.active, [Validators.required]),
      isGradeNotice: new FormControl(false),
      gradeId: new FormControl(),
      gradeName: new FormControl(),
      memberType: new FormControl(
        [
          SchoolMemberType.parent,
          SchoolMemberType.student,
          SchoolMemberType.teacher,
        ],
        [Validators.required],
      ),
    });
  }

  ngOnInit() {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.exit$))
      .subscribe((params) => {
        this.groupId = params.get('id');
        this.noticeId = params.get('noticeId');
        if (this.noticeId) {
          this.metadata.name = this.noticeId;
          this.metadata.noticeId = this.noticeId;
          this.store
            .select(noticeSelector.getNoticeById(this.noticeId))
            .pipe(takeUntil(this.exit$))
            .subscribe((notice) => {
              this.noticeData = notice;
              this.existingAttachments = notice?.attachments || [];
              this.currentNoticeImage = notice?.imageUrl;
              this.pageForm.patchValue({
                title: notice?.title,
                description: notice?.description,
                publishOn: notice?.publishOn
                  ? moment(notice.publishOn.toDate()).format('YYYY-MM-DDTHH:mm')
                  : null,
                removeAfter: notice?.removeAfter
                  ? moment(notice.removeAfter.toDate()).format(
                      'YYYY-MM-DDTHH:mm',
                    )
                  : null,
                noticeStatus: notice?.noticeStatus,
                memberType: notice?.memberType,
              });
            });
        }
      });

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        this.metadata.groupId = this.group?.id;
        if (!this.noticeId && this.group) {
          const defaultTypes =
            this.group.groupType === 'church'
              ? ['admin', 'member', 'treasurer']
              : [
                  SchoolMemberType.parent,
                  SchoolMemberType.student,
                  SchoolMemberType.teacher,
                ];
          this.pageForm.get('memberType')?.setValue(defaultTypes);
        }
      });

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.store
      .select(noticeSelector.inProgress)
      .pipe(takeUntil(this.exit$))
      .subscribe((working) => {
        this.working = working;
      });

    this.grades$ = this.store
      .select(gradeSelector.getGradesByGroup(this.groupId!, 'desc'))
      .pipe(takeUntil(this.exit$));

    this.store
      .select(groupSelector.getMyRolesInGroup(this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((roles) => {
        this.myRoles = roles;
        if (
          this.myRoles?.faculty &&
          !(this.myRoles?.management ?? false) &&
          this.group?.gradeId != null
        ) {
          this.pageForm.patchValue({
            isGradeNotice: true,
            gradeId: this.group?.gradeId,
            gradeName: this.group?.gradeName,
          });
          this.pageForm.get('isGradeNotice')?.updateValueAndValidity();
        }
        this.cdr.markForCheck();
      });
  }

  toggleVoiceInput(): void {
    if (this.isRecordingVoice) {
      this.stopVoiceInput();
      return;
    }

    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      this.toastr.warning(
        'Speech recognition is not supported in this browser.',
        'Voice Input',
      );
      return;
    }

    try {
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = false;
      this.recognition.lang = 'en-US';

      this.recognition.onstart = () => {
        this.isRecordingVoice = true;
        this.toastr.info(
          'Listening... speak your notice request.',
          'Voice Input',
        );
        this.cdr.markForCheck();
      };

      this.recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        if (transcript) {
          this.aiPromptInput = this.aiPromptInput
            ? `${this.aiPromptInput} ${transcript}`
            : transcript;
        }
        this.isRecordingVoice = false;
        this.cdr.markForCheck();
      };

      this.recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        this.isRecordingVoice = false;
        this.toastr.error(
          `Speech recognition error: ${event.error}`,
          'Voice Input',
        );
        this.cdr.markForCheck();
      };

      this.recognition.onend = () => {
        this.isRecordingVoice = false;
        this.cdr.markForCheck();
      };

      this.recognition.start();
    } catch (err: any) {
      this.isRecordingVoice = false;
      this.toastr.error('Failed to start voice recording.', 'Voice Input');
      this.cdr.markForCheck();
    }
  }

  stopVoiceInput(): void {
    if (this.recognition) {
      this.recognition.stop();
    }
    this.isRecordingVoice = false;
    this.cdr.markForCheck();
  }

  useQuickPrompt(promptText: string): void {
    this.aiPromptInput = promptText;
    this.submitAiPrompt();
  }

  /** Opens/closes the AI chat drawer on the right side of the page. */
  toggleChat(): void {
    this.chatOpen = !this.chatOpen;
    this.cdr.markForCheck();
    if (this.chatOpen) {
      this.scrollChatToBottom();
    }
  }

  // ─────────────────────────────────────────────
  // AI INTENT ROUTER
  // ─────────────────────────────────────────────

  async submitAiPrompt(): Promise<void> {
    const userPrompt = this.aiPromptInput.trim();
    if (!userPrompt || !this.groupId) return;

    this.aiGenerating = true;
    this.chatHistory.push({ role: 'user', text: userPrompt });
    this.aiPromptInput = '';
    this.cdr.markForCheck();
    this.scrollChatToBottom();

    try {
      const formattedHistory = this.chatHistory.slice(0, -1).map((msg) => ({
        role: msg.role === 'user' ? ('user' as const) : ('model' as const),
        parts: [{ text: msg.text }],
      }));

      const isAdmin = this.canSelectNoticeAudience();
      const isTeacherOnly =
        (this.myRoles?.faculty ?? false) && !(this.myRoles?.management ?? false);

      const result = await this.noticeService.classifyNoticeIntent({
        prompt: userPrompt,
        chatHistory: formattedHistory,
        currentFormState: this.getFormStateSummary(),
        groupName: this.group?.name,
        hasExistingImage: !!this.currentNoticeImage,
        hasDraft: this.hasDraft,
        userInfo: {
          isAdmin,
          isTeacherOnly,
          gradeId: this.group?.gradeId || this.pageForm.get('gradeId')?.value,
          gradeName: this.group?.gradeName || this.pageForm.get('gradeName')?.value,
          groupType: this.group?.groupType || 'school',
        },
        isImageEditorOpen: this.showImageEditor,
        imageLayers: this.getImageLayerContext(),
      });

      switch (result.toolName) {
        case 'generate_notice':
          await this.handleGenerateNotice(result.toolArgs, userPrompt);
          break;
        case 'patch_notice_fields':
          this.handlePatchFields(result.toolArgs);
          break;
        case 'generate_image':
          await this.handleGenerateImage(result.toolArgs);
          break;
        case 'open_image_editor':
          this.handleOpenImageEditor(result.toolArgs);
          break;
        case 'close_image_editor':
          this.handleCloseImageEditor(result.toolArgs);
          break;
        case 'edit_image_layer':
          this.handleEditImageLayer(result.toolArgs);
          break;
        case 'add_image_layer':
          this.handleAddImageLayer(result.toolArgs);
          break;
        case 'remove_image_layer':
          this.handleRemoveImageLayer(result.toolArgs);
          break;
        case 'save_notice':
          this.handleSaveIntent(result.toolArgs);
          break;
        case 'answer_question':
          this.handleAnswerQuestion(result.toolArgs);
          break;
        default:
          await this.handleGenerateNotice(result.toolArgs, userPrompt);
      }
    } catch (err: any) {
      console.error('Failed to process AI request:', err);
      this.toastr.error(
        'Failed to process your request: ' + (err.message || err),
        'AI Error',
      );
      this.chatHistory.push({
        role: 'assistant',
        text: 'Sorry, I encountered an error. Please try again.',
      });
    } finally {
      this.aiGenerating = false;
      this.cdr.markForCheck();
      this.scrollChatToBottom();
    }
  }

  /** Scrolls the AI chat log to the latest message. Runs on a macrotask so it fires after Angular has painted the new message. */
  private scrollChatToBottom(): void {
    setTimeout(() => {
      const el = this.chatContainerRef?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }

  // ─────────────────────────────────────────────
  // INTENT HANDLERS
  // ─────────────────────────────────────────────

  /** Full AI notice generation — auto-generates image only on first call unless user opts out */
  async handleGenerateNotice(args: any, userPrompt: string): Promise<void> {
    try {
      const formattedHistory = this.chatHistory.slice(0, -1).map((msg) => ({
        role: msg.role === 'user' ? ('user' as const) : ('model' as const),
        parts: [{ text: msg.text }],
      }));

      const isAdmin = this.canSelectNoticeAudience();
      const isTeacherOnly =
        (this.myRoles?.faculty ?? false) && !(this.myRoles?.management ?? false);
      const userInfo = {
        isAdmin,
        isTeacherOnly,
        gradeId: this.group?.gradeId || this.pageForm.get('gradeId')?.value,
        gradeName: this.group?.gradeName || this.pageForm.get('gradeName')?.value,
        groupType: this.group?.groupType || 'school',
      };

      const hasExistingImage = !!this.currentNoticeImage;

      const res = await this.noticeService.generateNoticeWithAi({
        prompt: userPrompt,
        groupId: this.groupId!,
        groupName: this.group?.name,
        shouldGenerateImage: false, // image handled separately below
        chatHistory: formattedHistory,
        userInfo,
        hasExistingImage,
      });

      const noticeData = res.noticeData;
      this.aiTextCallCount++;
      this.recordTextCreditUsage(res, noticeData.title);
      this.populateFormFromAiData(noticeData, userInfo);

      // Auto-generate image ONLY on first generation, unless user explicitly opted out
      const userLower = userPrompt.toLowerCase();
      const userOptedOutImage =
        userLower.includes('no image') ||
        userLower.includes('without image') ||
        userLower.includes("don't generate image") ||
        userLower.includes('dont generate image') ||
        userLower.includes("i'll upload") ||
        userLower.includes("upload my own");

      const isFirstGeneration = this.aiTextCallCount === 1;
      const shouldAutoImage = isFirstGeneration && !hasExistingImage && !userOptedOutImage;

      let generatedImageUrl: string | undefined;
      if (shouldAutoImage) {
        // Use AI's imageThemeDescription if available, fall back to notice title
        const themeForImage = noticeData.imageThemeDescription || noticeData.title;
        generatedImageUrl = await this.generateImageForNotice(
          { ...noticeData, imageThemeDescription: themeForImage },
          userPrompt,
        );
        // Deliberately does NOT switch to the Notice Image tab here — the
        // user asked to see the completed form with the generated image
        // thumbnail, and can open the tab themselves (or via the "Edit
        // image" chip) only when they actually want to change something.
        // The editor is still mounted (hidden) and given the background now,
        // so it's ready and showing the right thing the moment they do.
        if (generatedImageUrl) {
          this.syncBackgroundIntoImageEditor(generatedImageUrl);
        }
      } else if (hasExistingImage) {
        generatedImageUrl = this.currentNoticeImage || undefined;
      }

      this.hasDraft = true;
      this.aiImageTheme = noticeData.imageThemeDescription || '';

      const chatActions: { label: string; icon: string; type: 'edit_image' | 'save' }[] = [
        { label: generatedImageUrl ? 'Edit image' : 'Open image editor', icon: 'fa fa-image', type: 'edit_image' },
        { label: 'Save Notice', icon: 'fa fa-save', type: 'save' },
      ];

      this.chatHistory.push({
        role: 'assistant',
        text: `✅ Draft ready! "${noticeData.title}"`,
        noticeData,
        imageUrl: generatedImageUrl,
        noticePreview: {
          title: noticeData.title,
          description: (noticeData.description || '').slice(0, 130) +
            ((noticeData.description || '').length > 130 ? '...' : ''),
        },
        chatActions,
      });
      this.cdr.markForCheck();
    } catch (err: any) {
      this.chatHistory.push({
        role: 'assistant',
        text: 'Sorry, I ran into an error generating the notice. Please try again.',
      });
      throw err;
    }
  }

  /** Patches only the specific fields the user mentioned — no full re-generation */
  handlePatchFields(args: any): void {
    const { assistantMessage, ...fieldUpdates } = args;
    const patch: any = {};

    if (fieldUpdates.title !== undefined) patch.title = fieldUpdates.title;
    if (fieldUpdates.description !== undefined) patch.description = fieldUpdates.description;
    if (fieldUpdates.publishOn !== undefined) patch.publishOn = fieldUpdates.publishOn;
    if (fieldUpdates.removeAfter !== undefined) patch.removeAfter = fieldUpdates.removeAfter;
    if (fieldUpdates.noticeStatus !== undefined) patch.noticeStatus = fieldUpdates.noticeStatus;
    if (fieldUpdates.memberType !== undefined) patch.memberType = fieldUpdates.memberType;
    if (fieldUpdates.isGradeNotice !== undefined) {
      patch.isGradeNotice = fieldUpdates.isGradeNotice;
      if (fieldUpdates.gradeId !== undefined) patch.gradeId = fieldUpdates.gradeId;
      if (fieldUpdates.gradeName !== undefined) patch.gradeName = fieldUpdates.gradeName;
    }

    if (Object.keys(patch).length > 0) {
      this.pageForm.patchValue(patch);
      if (patch.isGradeNotice !== undefined) this.onGradeNotice();
    }

    this.chatHistory.push({
      role: 'assistant',
      text: assistantMessage || 'Done! The notice has been updated.',
      chatActions: [
        { label: 'Save Notice', icon: 'fa fa-save', type: 'save' },
      ],
    });
  }

  /** Generates a brand-new background image and opens the editor with it. */
  async handleGenerateImage(args: any): Promise<void> {
    this.chatHistory.push({
      role: 'assistant',
      text: args.assistantMessage || 'Generating a new background for your notice image...',
    });
    this.openImageEditor();
    this.cdr.markForCheck();

    const imageUrl = await this.generateImageOnly(args.imageTheme);

    if (imageUrl) {
      try {
        this.imageEditorRef?.applyAiCommand({
          type: 'set-background',
          sourceUrl: imageUrl,
        });
      } catch {
        this.chatHistory.push({
          role: 'assistant',
          text: "I've generated the background — the editor's layer tools are still being built, so fine-tuning text and QR codes isn't available quite yet.",
        });
      }
    }
    this.cdr.markForCheck();
  }

  /** Opens the image editor without generating or changing anything. */
  handleOpenImageEditor(args: any): void {
    this.chatHistory.push({
      role: 'assistant',
      text: args.assistantMessage || 'Opening the image editor...',
    });
    this.openImageEditor();
  }

  /** Closes the image editor and returns to the form. */
  handleCloseImageEditor(args: any): void {
    this.chatHistory.push({
      role: 'assistant',
      text: args.assistantMessage || 'Closed the image editor.',
    });
    this.closeImageEditor();
  }

  /** Modifies an existing layer on the open image editor. */
  handleEditImageLayer(args: any): void {
    this.openImageEditor();
    try {
      this.imageEditorRef?.applyAiCommand({
        type: 'update-layer',
        layerId: args.layerId,
        props: args.updates || {},
      });
      this.chatHistory.push({
        role: 'assistant',
        text: args.assistantMessage || 'Updated.',
      });
    } catch {
      this.chatHistory.push({
        role: 'assistant',
        text: "I've opened the editor, but I can't apply that change yet — the layer tools are still being built.",
      });
    }
    this.cdr.markForCheck();
  }

  /** Adds a new layer (text, QR, logo, decoration) to the open image editor. */
  handleAddImageLayer(args: any): void {
    this.openImageEditor();
    try {
      this.imageEditorRef?.applyAiCommand({
        type: 'add-layer',
        layerType: args.layerType,
        props: args.props || {},
      });
      this.chatHistory.push({
        role: 'assistant',
        text: args.assistantMessage || 'Added.',
      });
    } catch {
      this.chatHistory.push({
        role: 'assistant',
        text: "I've opened the editor, but I can't add that layer yet — the layer tools are still being built.",
      });
    }
    this.cdr.markForCheck();
  }

  /** Removes an existing layer from the open image editor. */
  handleRemoveImageLayer(args: any): void {
    this.openImageEditor();
    try {
      this.imageEditorRef?.applyAiCommand({
        type: 'remove-layer',
        layerId: args.layerId,
      });
      this.chatHistory.push({
        role: 'assistant',
        text: args.assistantMessage || 'Removed.',
      });
    } catch {
      this.chatHistory.push({
        role: 'assistant',
        text: "I've opened the editor, but I can't remove that layer yet — the layer tools are still being built.",
      });
    }
    this.cdr.markForCheck();
  }

  /** Calls upsert() directly — no AI generation */
  handleSaveIntent(args: any): void {
    this.chatHistory.push({
      role: 'assistant',
      text: args.assistantMessage || 'Saving your notice now...',
    });
    this.activeTab = 'form';
    this.cdr.markForCheck();
    // Small delay so the user sees the chat reply before we navigate away
    setTimeout(() => this.upsert(), 150);
  }

  /** Replies with information without modifying any fields */
  handleAnswerQuestion(args: any): void {
    this.chatHistory.push({
      role: 'assistant',
      text: args.assistantMessage || "I'm not sure — could you rephrase your question?",
    });
  }

  /** Called when user clicks an action chip inside a chat message */
  handleChatAction(type: 'edit_image' | 'save'): void {
    switch (type) {
      case 'edit_image':
        this.openImageEditor();
        break;
      case 'save':
        this.upsert();
        break;
    }
  }

  // ─────────────────────────────────────────────
  // IMAGE EDITOR TAB
  // ─────────────────────────────────────────────

  /**
   * Switches to the "Notice Image" tab, on the SAME component instance —
   * never a route change — so the form and chat state are never lost. See
   * views/components/image-editor/IMAGE_EDITOR_REQUIREMENTS.md.
   *
   * Uses detectChanges() (synchronous) rather than markForCheck() because
   * callers frequently need imageEditorRef populated immediately afterwards
   * (e.g. to call applyAiCommand right after opening) — markForCheck alone
   * only schedules a future check, so the ViewChild wouldn't be resolved
   * yet on the very next line.
   */
  openImageEditor(): void {
    this.imageEditorMounted = true;
    this.activeTab = 'image';
    this.cdr.detectChanges();
    this.imageEditorRef?.zoomToFit();
  }

  /**
   * Mounts the editor (hidden, without switching tabs) and pushes a
   * background layer into it — used by flows that generate an image but
   * deliberately don't want to yank the user into the Notice Image tab
   * (e.g. the first AI draft). Safe to call before the editor has ever been
   * opened: mounting is idempotent and `applyAiCommand` replaces an existing
   * background layer in place rather than duplicating it.
   */
  private syncBackgroundIntoImageEditor(sourceUrl: string): void {
    this.imageEditorMounted = true;
    this.cdr.detectChanges();
    this.imageEditorRef?.applyAiCommand({ type: 'set-background', sourceUrl });
  }

  closeImageEditor(): void {
    this.activeTab = 'form';
    this.cdr.markForCheck();
  }

  onImageEditorSaved(result: {
    imageUrl: string;
    thumbnailUrl?: string;
    scene: EditorScene;
  }): void {
    this.currentNoticeImage = result.imageUrl;
    this.currentImageScene = result.scene;
    this.activeTab = 'form';
    this.cdr.markForCheck();
  }

  onImageEditorCancelled(): void {
    this.activeTab = 'form';
    this.cdr.markForCheck();
  }

  /** Layer summaries for the currently-open editor, used as AI chat context so intents like "make the title bigger" can be grounded in a real layerId. */
  getImageLayerContext(): LayerSummary[] {
    return this.imageEditorRef?.getLayerSummaries() ?? [];
  }

  // ─────────────────────────────────────────────
  // IMAGE GENERATION HELPERS
  // ─────────────────────────────────────────────

  /** Generates image for a notice draft — stores result in currentNoticeImage */
  async generateImageForNotice(
    noticeData: NoticeAiData,
    userPrompt: string,
  ): Promise<string | undefined> {
    try {
      this.aiImageGenerating = true;
      this.cdr.markForCheck();

      const posterPrompt = await this.noticeService.generateNoticePosterPrompt({
        noticeTitle: noticeData.title,
        noticeDescription: noticeData.description,
        imageThemeDescription: noticeData.imageThemeDescription,
        userPrompt,
        groupName: this.group?.name,
      });

      this.latestGeneratedStylePrompt = posterPrompt;
      this.styleNameInput = `${noticeData.title.slice(0, 25)} Style`;

      const imageUrl = await this.noticeService.generateNoticeImage(
        posterPrompt,
        undefined,
        this.groupId!,
        noticeData.title,
        this.noticeData?.id ?? this.uuid,
      );
      this.currentNoticeImage = imageUrl;
      return imageUrl;
    } catch (err: any) {
      console.warn('Image generation failed:', err);
      this.toastr.warning('Image generation failed. You can add one later.', 'Notice Image');
      return undefined;
    } finally {
      this.aiImageGenerating = false;
      this.cdr.markForCheck();
    }
  }

  /** Core image-only generation — used by both the generate_image tool handler and the editor's regenerate action */
  async generateImageOnly(theme?: string): Promise<string | undefined> {
    if (!this.groupId) return undefined;
    this.aiImageGenerating = true;
    this.cdr.markForCheck();

    try {
      const title = this.pageForm.get('title')?.value || 'Notice';
      const description = this.pageForm.get('description')?.value || '';
      const imageTheme = theme || this.aiImageTheme || '';

      const posterPrompt = await this.noticeService.generateNoticePosterPrompt({
        noticeTitle: title,
        noticeDescription: description,
        imageThemeDescription: imageTheme,
        userPrompt: imageTheme,
        groupName: this.group?.name,
      });

      this.latestGeneratedStylePrompt = posterPrompt;
      if (theme) this.aiImageTheme = theme;

      const imageUrl = await this.noticeService.generateNoticeImage(
        posterPrompt,
        undefined,
        this.groupId,
        title,
        this.noticeData?.id ?? this.uuid,
      );
      this.currentNoticeImage = imageUrl;
      return imageUrl;
    } catch (err: any) {
      this.toastr.error(
        'Failed to generate image: ' + (err.message || err),
        'Image Error',
      );
      return undefined;
    } finally {
      this.aiImageGenerating = false;
      this.cdr.markForCheck();
    }
  }

  // ─────────────────────────────────────────────
  // FORM & STATE HELPERS
  // ─────────────────────────────────────────────

  /** Populates form fields from AI-generated notice data, respecting role constraints */
  populateFormFromAiData(
    noticeData: NoticeAiData,
    userInfo: { isAdmin: boolean; isTeacherOnly: boolean; gradeId?: string; gradeName?: string; groupType?: string },
  ): void {
    let validMemberTypes: string[];
    if (this.group?.groupType === 'church') {
      validMemberTypes = ['admin', 'member', 'treasurer'];
    } else {
      validMemberTypes = [
        SchoolMemberType.parent,
        SchoolMemberType.student,
        SchoolMemberType.teacher,
      ];
    }

    this.pageForm.patchValue({
      title: noticeData.title,
      description: noticeData.description,
      publishOn: noticeData.publishOn || dayjs().format('YYYY-MM-DDTHH:mm'),
      removeAfter:
        noticeData.removeAfter || dayjs().add(30, 'days').format('YYYY-MM-DDTHH:mm'),
      noticeStatus: noticeData.noticeStatus || NoticeStatus.active,
    });

    if (this.group?.groupType === 'church') {
      this.pageForm.patchValue({ isGradeNotice: false, gradeId: null, gradeName: null });
    } else if (!userInfo.isAdmin && userInfo.isTeacherOnly) {
      this.pageForm.patchValue({
        isGradeNotice: true,
        gradeId: userInfo.gradeId,
        gradeName: userInfo.gradeName,
      });
      this.onGradeNotice();
    } else {
      this.pageForm.patchValue({ isGradeNotice: noticeData.isGradeNotice || false });
      if (noticeData.isGradeNotice && noticeData.gradeId) {
        this.pageForm.patchValue({
          gradeId: noticeData.gradeId,
          gradeName: noticeData.gradeName,
        });
        this.onGradeNotice();
      }
    }

    if (noticeData.memberType && noticeData.memberType.length > 0) {
      const filtered = noticeData.memberType.filter((t) => validMemberTypes.includes(t));
      this.pageForm
        .get('memberType')
        ?.setValue(filtered.length > 0 ? filtered : validMemberTypes);
    } else {
      this.pageForm.get('memberType')?.setValue(validMemberTypes);
    }
  }

  /** Returns a human-readable summary of the current form state for AI context */
  getFormStateSummary(): string {
    const v = this.pageForm.value;
    const lines: string[] = [];
    if (v.title) lines.push(`Title: "${v.title}"`);
    if (v.description)
      lines.push(
        `Description: "${v.description.slice(0, 200)}${v.description.length > 200 ? '...' : ''}"`,
      );
    if (v.publishOn) lines.push(`Publish On: ${v.publishOn}`);
    if (v.removeAfter) lines.push(`Remove After: ${v.removeAfter}`);
    if (v.noticeStatus) lines.push(`Status: ${v.noticeStatus}`);
    if (v.memberType?.length) lines.push(`Audience: ${v.memberType.join(', ')}`);
    if (v.isGradeNotice) lines.push(`Grade Notice: Yes (Grade: ${v.gradeName || 'Not set'})`);
    if (this.currentNoticeImage) lines.push(`Has image: Yes`);
    return lines.length ? lines.join('\n') : 'No notice drafted yet.';
  }

  /**
   * Records AI text credit usage according to the tiered pricing rules.
   * The description references the notice's own title/id (a business identifier the member will
   * recognize) rather than raw chat prompt text, an internal vendor name, or a bare groupId — so the
   * transaction is something a member could point to if they wanted to raise a dispute.
   */
  private recordTextCreditUsage(res: any, noticeTitle?: string): void {
    if (!this.groupId) return;
    const id = this.noticeData?.id ?? this.uuid;
    const title = noticeTitle || this.pageForm.get('title')?.value || 'Untitled Notice';
    const businessReference = `Notice: "${title}" (ID: ${id})`;

    if (this.aiTextCallCount === 1) {
      this.noticeService
        .recordAiCreditUsage(
          this.groupId,
          'notice_generation',
          0.01,
          `${businessReference} — initial draft`,
        )
        .catch((err) =>
          console.warn('NoticeService :: Failed to record micro-floor credits:', err),
        );
    } else if (this.aiTextCallCount >= 6) {
      const exactCredits = res.exactCredits || 0.005;
      this.noticeService
        .recordAiCreditUsage(
          this.groupId,
          'notice_generation',
          exactCredits,
          `${businessReference} — revision #${this.aiTextCallCount}`,
        )
        .catch((err) =>
          console.warn('NoticeService :: Failed to record exact text AI credits:', err),
        );
    }
  }

  async saveNoticeStyle(isDefault: boolean = false): Promise<void> {
    const styleName =
      this.styleNameInput?.trim() ||
      `${this.pageForm.get('title')?.value?.slice(0, 25) || 'Notice'} Style`;
    if (!styleName || !this.groupId) return;
    this.isSavingStyle = true;
    this.cdr.markForCheck();

    try {
      await this.noticeService.saveGroupNoticeSkill(
        this.groupId,
        {
          name: styleName,
          promptTemplate:
            this.latestGeneratedStylePrompt ||
            'Default notice image prompt style',
          createdBy: this.user?.name,
          createdById: this.user?.uid,
        },
        isDefault,
      );
      this.styleSavedSuccessfully = true;
      this.toastr.success(
        isDefault
          ? 'Notice image style saved and set as default style!'
          : 'Notice image style saved to your styles database!',
        'Style Saved',
      );
      this.closeSaveStyleDialog();
    } catch (err: any) {
      this.toastr.error(
        'Failed to save style: ' + (err.message || err),
        'Error',
      );
    } finally {
      this.isSavingStyle = false;
      this.cdr.markForCheck();
    }
  }

  closeSaveStyleDialog(): void {
    this.showSaveStyleDialog = false;
    this.cdr.markForCheck();
  }

  async onImageUploadStatus(result: {
    success: boolean;
    response?: any;
    error?: any;
  }): Promise<void> {
    if (result.success) {
      // The new file is already uploaded by this point (app-s3-image-upload
      // uploads on selection, before this callback fires) — but nothing has
      // been saved to the notice yet, so this is still the right/last place
      // to confirm before we overwrite whatever image the notice already has.
      if (this.currentNoticeImage) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = {
          title: 'Replace existing image?',
          description:
            'This notice already has an image. Uploading this new one will replace it — the existing image will no longer be used on this notice.',
          action: 'Replace',
          cancelText: 'Cancel',
          showType: 'warning',
        };
        const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);
        const confirmed = await firstValueFrom(dialogRef.afterClosed());
        if (!confirmed) {
          this.working = false;
          this.cdr.markForCheck();
          return;
        }
      }

      const id = this.noticeData?.id ?? this.uuid;
      this.uploadAttachments(id)
        .then((newUploaded) => {
          this.createDatabaseEntry(
            id,
            result.response?.uploadURL || this.currentNoticeImage || this.noticeData?.imageUrl,
            newUploaded,
          );
        })
        .catch((err) => {
          this.working = false;
          this.toastr.error(
            `Failed to upload attachments. ${err.message || err}`,
            'Upload Error',
          );
        });
    } else {
      this.working = false;
      this.toastr.error(
        `Failed to upload image. ${result.error}`,
        'Image Upload Error',
      );
    }
  }

  ngAfterViewInit(): void {}

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots =
      5 - (this.existingAttachments.length + this.newAttachments.length);
    if (remainingSlots <= 0) {
      this.toastr.warning('Maximum upload limit of 5 attachments reached.');
      return;
    }

    const filesToUpload = Array.from(files).slice(0, remainingSlots);

    filesToUpload.forEach((file) => {
      if (file.size > 5 * 1024 * 1024) {
        this.toastr.error(`${file.name} exceeds the 5MB size limit.`);
        return;
      }

      this.newAttachments.push({
        file,
        name: file.name,
        progress: 0,
        uploading: false,
      });
    });

    event.target.value = '';
    this.cdr.markForCheck();
  }

  removeNewAttachment(index: number): void {
    this.newAttachments.splice(index, 1);
    this.cdr.markForCheck();
  }

  async dialogConfirm(title: string, description: string): Promise<boolean> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title,
      description,
      action: 'Delete',
      cancelText: 'Cancel',
      showType: 'warning',
    };
    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);
    return firstValueFrom(dialogRef.afterClosed());
  }

  async deleteExistingAttachment(
    attachment: NoticeAttachment,
    index: number,
  ): Promise<void> {
    const confirmed = await this.dialogConfirm(
      'Delete Attachment',
      `Are you sure you want to delete ${attachment.name}?`,
    );
    if (!confirmed) return;

    this.working = true;
    this.cdr.markForCheck();

    try {
      const headers = await this.utilsService.setAxiosHeaders(true);
      await axios.post(
        environment.AWS_SCHOOL_NOTICE_DELETE_ATTACHMENT,
        {
          groupId: this.groupId,
          noticeId: this.noticeId,
          fileName: attachment.name,
        },
        {
          headers: headers,
        },
      );

      const updatedAttachments = [...this.existingAttachments];
      updatedAttachments.splice(index, 1);
      this.existingAttachments = updatedAttachments;

      const noticeRef = doc(
        this.afs,
        'groups',
        this.groupId!,
        'notices',
        this.noticeId!,
      );
      await updateDoc(noticeRef, { attachments: this.existingAttachments });

      this.store.dispatch(
        noticeActions.updateSuccess({
          notice: {
            ...this.noticeData,
            attachments: this.existingAttachments,
          } as Notice,
        }),
      );
      this.toastr.success('Attachment deleted successfully.', 'Deleted');
    } catch (err: any) {
      console.error(err);
      this.toastr.error(
        'Failed to delete attachment: ' + (err.message || err),
        'Error',
      );
    } finally {
      this.working = false;
      this.cdr.markForCheck();
    }
  }

  async uploadAttachments(noticeId: string): Promise<NoticeAttachment[]> {
    const uploaded: NoticeAttachment[] = [];

    for (let item of this.newAttachments) {
      item.uploading = true;
      item.error = undefined;
      this.cdr.markForCheck();

      try {
        const headers = await this.utilsService.setAxiosHeaders(true);
        const response = await axios.get<any>(
          environment.AWS_SCHOOL_NOTICE_ATTACHMENT_SIGNEDURL,
          {
            params: {
              groupId: this.groupId,
              noticeId: noticeId,
              fileName: item.file.name,
            },
            headers: headers,
          },
        );

        if (response.status !== 200) {
          throw new Error('Failed to get signed URL');
        }

        const data = response.data;
        const uploadUrl = data.url;

        await axios.put(uploadUrl, item.file, {
          headers: {
            'Content-Type': item.file.type,
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              item.progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              this.cdr.markForCheck();
            }
          },
        });

        uploaded.push({
          name: item.file.name,
          path: new URL(uploadUrl).pathname.substring(1),
          url: UtilsService.replaceDomain(uploadUrl, this.cloudFrontDomain),
        });

        item.progress = 100;
        item.uploading = false;
      } catch (error: any) {
        item.uploading = false;
        item.error = error.message || 'Upload failed';
        this.cdr.markForCheck();
        throw error;
      }
    }

    return uploaded;
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  canSelectNoticeAudience(): boolean {
    if (this.group?.groupType === 'church') {
      return this.myRoles?.admin === true;
    }
    return this.myRoles?.management === true;
  }

  isSubmitDisabled(): boolean {
    if (this.pageForm.invalid || !this.group) return true;
    if (this.group.groupType === 'church') {
      return false;
    }
    return (
      this.myRoles?.faculty && !this.myRoles?.management && !this.group?.gradeId
    );
  }

  onMemberTypeChange(item: string): void {
    const currMemberType = this.pageForm.get('memberType')?.value || [];

    let updatedMemberType: string[];

    if (currMemberType.includes(item)) {
      updatedMemberType = currMemberType.filter((r: string) => r !== item);
    } else {
      updatedMemberType = [...currMemberType, item]; // creates a new array
    }

    this.pageForm.get('memberType')?.setValue(updatedMemberType);
  }

  onGradeNotice() {
    if (
      this.myRoles?.faculty &&
      !(this.myRoles?.management ?? false) &&
      !this.group?.gradeId
    ) {
      this.onShowAlert({
        title: 'Permission Denied',
        message: `You are only allowed to create grade notices for your own grade.`,
      });
      return;
    }

    if (this.pageForm.get('isGradeNotice')?.value) {
      this.pageForm.get('gradeId')?.setValidators([Validators.required]);
      this.pageForm.get('gradeId')?.updateValueAndValidity();
      this.pageForm.get('gradeName')?.setValidators([Validators.required]);
      this.pageForm.get('gradeName')?.updateValueAndValidity();
      if (this.myRoles?.faculty && this.group?.gradeId) {
        this.pageForm.patchValue({
          gradeId: this.group?.gradeId,
          gradeName: this.group?.gradeName,
        });
      } else {
        this.grades$.pipe(take(1)).subscribe((grades: Grade[]) => {
          const grade = grades[0];
          if (grade == undefined) return;
          this.pageForm.patchValue({
            gradeId: grade.id,
            gradeName: grade.gradeName,
          });
        });
      }
    } else {
      this.pageForm.get('gradeId')?.clearValidators();
      this.pageForm.get('gradeId')?.updateValueAndValidity();
      this.pageForm.get('gradeName')?.clearValidators();
      this.pageForm.get('gradeName')?.updateValueAndValidity();
      this.pageForm.get('gradeId')?.setValue(null);
      this.pageForm.get('gradeName')?.setValue(null);
    }
  }

  onChangeGradeOption(event: any) {
    const gradeId = event.target.value;
    this.grades$.pipe(take(1)).subscribe((grades: Grade[]) => {
      const grade = grades.find((g: Grade) => g.id === gradeId);
      if (grade == undefined) return;
      this.pageForm.patchValue({
        gradeId: grade.id,
        gradeName: grade.gradeName,
      });
    });
  }

  upsert() {
    this.logger.debug(
      'Upserting notice: ',
      JSON.stringify(this.pageForm.value, null, 2),
    );

    if (this.pageForm.invalid || !this.group) return;

    const uuid = this.noticeData?.id ?? this.uuid;

    this.working = true;

    if (this.imageUploader?.uppy && this.imageUploader.uppy.getFiles().length > 0) {
      this.imageUploader.upload();
    } else {
      this.uploadAttachments(uuid)
        .then((newUploaded) => {
          const imageToPass =
            this.currentNoticeImage !== undefined
              ? (this.currentNoticeImage ?? undefined)
              : this.noticeData?.imageUrl;
          this.createDatabaseEntry(uuid, imageToPass, newUploaded);
        })
        .catch((err) => {
          this.working = false;
          this.toastr.error(
            `Failed to upload attachments. ${err.message || err}`,
            'Upload Error',
          );
        });
    }
  }

  async uploadBase64ImageToS3(
    dataUrl: string,
    noticeId: string,
  ): Promise<string> {
    const groupId = this.groupId || this.group?.id;
    if (!groupId) {
      throw new Error('Group ID is required to upload notice image.');
    }
    const uid = this.user?.uid || this.fbAuth.currentUser?.uid || '';
    const isPng = dataUrl.startsWith('data:image/png');
    const fileExtn = isPng ? 'png' : 'jpg';
    const contentType = isPng ? 'image/png' : 'image/jpeg';

    // 1. Convert data URL / remote fallback URL to Blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    // 2. Request S3 Pre-Signed PUT URL
    const headers = await this.utilsService.setAxiosHeaders(
      true,
      undefined,
      this.apiKey,
    );
    const signedUrlResponse = await axios.get<{ url: string }>(
      this.preSignedUrlEndpoint,
      {
        params: {
          uid,
          name: noticeId,
          fileExtension: fileExtn,
          noticeId: noticeId,
          groupId: groupId,
          aspectRatio: '1',
        },
        headers,
      },
    );

    if (signedUrlResponse.status !== 200 || !signedUrlResponse.data?.url) {
      throw new Error('Failed to get an upload URL from AWS.');
    }

    const uploadUrl = signedUrlResponse.data.url;

    // 3. Upload binary to S3 via PUT
    await axios.put(uploadUrl, blob, {
      headers: { 'Content-Type': contentType },
    });

    // 4. Return CloudFront domain URL
    let imageUrl = this.cloudFrontDomain
      ? UtilsService.replaceDomain(uploadUrl, this.cloudFrontDomain)
      : uploadUrl;

    // If updating an existing notice that was previously modified, append document version
    if (this.noticeData?.modified) {
      const v =
        typeof (this.noticeData.modified as any).toMillis === 'function'
          ? (this.noticeData.modified as any).toMillis()
          : Date.now();
      imageUrl = `${imageUrl}?v=${v}`;
    }

    return imageUrl;
  }

  async createDatabaseEntry(
    id: string,
    url?: string | undefined,
    newAttachments: NoticeAttachment[] = [],
  ) {
    let imageUrlToSave =
      url !== undefined
        ? url
        : this.currentNoticeImage !== undefined
          ? (this.currentNoticeImage ?? undefined)
          : this.noticeData?.imageUrl;

    if (
      imageUrlToSave &&
      (imageUrlToSave.startsWith('data:') ||
        imageUrlToSave.startsWith('https://image.pollinations.ai'))
    ) {
      try {
        imageUrlToSave = await this.uploadBase64ImageToS3(
          imageUrlToSave,
          id,
        );
        this.currentNoticeImage = imageUrlToSave;
      } catch (err: any) {
        console.error('Failed to upload notice image to S3:', err);
        this.toastr.error(
          `Failed to upload image. ${err.message || err}`,
          'Upload Error',
        );
        this.working = false;
        return;
      }
    }

    let result: any = {
      ...this.pageForm.value,
      groupId: this.group?.id,
      groupName: this.group?.name,
      academicYear: this.group?.academicYear,
      groupType: this.group?.groupType ?? GroupType.school,
      createdBy: this.noticeData?.createdBy ?? this.user?.name,
      createdById: this.noticeData?.createdById ?? this.user?.uid,
      updatedBy: this.user?.name,
      updatedById: this.user?.uid,
      publishOn: Timestamp.fromDate(
        moment(this.pageForm.get('publishOn')?.value).toDate(),
      ),
      removeAfter: Timestamp.fromDate(
        moment(this.pageForm.get('removeAfter')?.value).toDate(),
      ),
      events: null,
      attachments: [...this.existingAttachments, ...newAttachments],
    };

    if (imageUrlToSave) {
      result.imageUrl = imageUrlToSave;
    } else if (imageUrlToSave === null) {
      result.imageUrl = null;
    }

    this.logger.debug('Upserting notice:', JSON.stringify(result, null, 2));

    if (this.noticeData?.id) {
      this.store.dispatch(
        noticeActions.update({
          groupId: this.groupId!,
          id: this.noticeData.id,
          notice: result,
        }),
      );
    } else {
      this.store.dispatch(noticeActions.create({ id: id, notice: result }));
    }
    this.working = false;

    if (
      (this.currentNoticeImage || this.latestGeneratedStylePrompt) &&
      !this.styleSavedSuccessfully
    ) {
      if (!this.styleNameInput) {
        this.styleNameInput = `${this.pageForm.get('title')?.value?.slice(0, 25) || 'Notice'} Style`;
      }
      this.showSaveStyleDialog = true;
      this.cdr.markForCheck();
    }
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'User Not Found',
      description: `Your logged in status was not found. Please login and try again.`,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
